import React, { useState } from 'react';
import { useTranslation } from '@/locales';

// 测试组件 - 会抛出错误
function ErrorComponent() {
    // 故意抛出一个错误来测试 ErrorBoundary
    throw new Error('这是一个测试错误，用于验证 ErrorBoundary 功能');
}

// 异步错误组件
function AsyncErrorComponent() {
    const [shouldThrow, setShouldThrow] = useState(false);
    
    React.useEffect(() => {
        if (shouldThrow) {
            // 模拟异步错误
            setTimeout(() => {
                throw new Error('这是一个异步错误');
            }, 1000);
        }
    }, [shouldThrow]);
    
    return (
        <button 
            onClick={() => setShouldThrow(true)}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
            触发异步错误
        </button>
    );
}

// 网络错误模拟组件
function NetworkErrorComponent() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    const simulateNetworkError = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // 模拟网络请求失败
            const response = await fetch('/api/nonexistent-endpoint');
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : '网络请求失败');
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className="space-y-2">
            <button 
                onClick={simulateNetworkError}
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            >
                {loading ? '请求中...' : '模拟网络错误'}
            </button>
            {error && (
                <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    网络错误: {error}
                </div>
            )}
        </div>
    );
}

export default function ErrorTestPage() {
    const { t } = useTranslation();
    const [showErrorComponent, setShowErrorComponent] = useState(false);
    
    return (
        <div className="max-w-4xl mx-auto p-6 space-y-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                    错误处理测试页面
                </h1>
                
                <div className="space-y-6">
                    {/* React 错误边界测试 */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <h2 className="text-lg font-semibold mb-3">React ErrorBoundary 测试</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                            点击按钮会触发一个 React 组件错误，测试 ErrorBoundary 是否能正确捕获并显示错误界面。
                        </p>
                        
                        {showErrorComponent ? (
                            <ErrorComponent />
                        ) : (
                            <button 
                                onClick={() => setShowErrorComponent(true)}
                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                                触发 React 错误
                            </button>
                        )}
                    </div>
                    
                    {/* 异步错误测试 */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <h2 className="text-lg font-semibold mb-3">异步错误测试</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                            异步错误通常不会被 ErrorBoundary 捕获，需要其他方式处理。
                        </p>
                        <AsyncErrorComponent />
                    </div>
                    
                    {/* 网络错误测试 */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <h2 className="text-lg font-semibold mb-3">网络错误测试</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                            模拟网络请求失败，测试错误处理机制。
                        </p>
                        <NetworkErrorComponent />
                    </div>
                    
                    {/* 路由错误测试 */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <h2 className="text-lg font-semibold mb-3">路由错误测试</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                            测试访问不存在的路由时的错误处理。
                        </p>
                        <div className="space-x-2">
                            <a 
                                href="#/nonexistent-page" 
                                className="inline-block px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                            >
                                访问不存在的页面 (404)
                            </a>
                            <a 
                                href="#/admin/restricted" 
                                className="inline-block px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                            >
                                访问受限页面 (403)
                            </a>
                        </div>
                    </div>
                    
                    {/* 错误恢复测试 */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <h2 className="text-lg font-semibold mb-3">错误恢复测试</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                            测试错误发生后的恢复机制。
                        </p>
                        <button 
                            onClick={() => setShowErrorComponent(false)}
                            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        >
                            重置错误状态
                        </button>
                    </div>
                </div>
                
                <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">测试说明</h3>
                    <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                        <li>• React 错误会被 ErrorBoundary 捕获并显示友好的错误界面</li>
                        <li>• 异步错误需要在代码中手动处理</li>
                        <li>• 网络错误通过 try-catch 捕获并显示</li>
                        <li>• 路由错误会显示相应的 404、403 等错误页面</li>
                        <li>• 开发环境会显示详细的错误信息，生产环境只显示用户友好的信息</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}